import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { IconLink } from '@tabler/icons-react';
import BaseTooltip from '../../../ClipCustomizer/pocAIEdits/components/BaseTooltip';
import ZoomEffectGraph from './ZoomEffectGraph';
import SpeakerLabelNumberInput from '@/App/remotion/components/SpeakerLabel/SpeakerLabelTypographyMenu/SpeakerLabelNumberInput';
import DualRangeSlider from '@/components/atoms/DualRangeSlider';
import TooltipV2 from '@/components/TooltipV2';
import IconButton from '@/components/atoms/Button/IconButton';
import { Effect, EffectType } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';

interface ZoomEffectTooltipProps {
  effect: Effect;
  triggerRef: React.RefObject<HTMLDivElement>;
  onSubmit: (
    toScale: number,
    baseScale: number,
    centerX: number,
    centerY: number,
    animationInDuration: number,
    animationOutDuration: number,
    animationType: 'easeInAndOut' | 'linear' | 'bounce'
  ) => void;
  onDelete: (effectId: string) => void;
}

const ZoomEffectTooltip: React.FC<ZoomEffectTooltipProps> = ({ effect, triggerRef, onSubmit, onDelete }) => {
  const [toScale, setToScale] = useState(effect.params.toScale.toString());
  const [baseScale, setBaseScale] = useState(effect.params.baseScale.toString());
  const [centerX, setCenterX] = useState((effect.params.centerX ?? 0.5).toString());
  const [centerY, setCenterY] = useState((effect.params.centerY ?? 0.5).toString());
  const [animationInDuration, setAnimationInDuration] = useState((effect.animationInDuration ?? 0).toFixed(2));
  const [animationOutDuration, setAnimationOutDuration] = useState((effect.animationOutDuration ?? 0).toFixed(2));
  const [animationType] = useState(effect.animationType || 'easeInAndOut');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<string>('');

  const totalDuration = useMemo(() => parseFloat(effect.duration.toFixed(2)), [effect.duration]);

  // Sync local state when effect prop changes (during live resize)
  useEffect(() => {
    setToScale(effect.params.toScale.toString());
    setBaseScale(effect.params.baseScale.toString());
    setCenterX((effect.params.centerX ?? 0.5).toString());
    setCenterY((effect.params.centerY ?? 0.5).toString());
    setAnimationInDuration((effect.animationInDuration ?? 0).toFixed(2));
    setAnimationOutDuration((effect.animationOutDuration ?? 0).toFixed(2));
  }, [effect]);

  // Helper functions to convert between scale and percentage for SpeakerLabelNumberInput
  const formatScaleOutput = useCallback((numValue: number): string => {
    return (numValue / 100).toString();
  }, []);

  const parseScaleInput = useCallback((value: string): number => {
    return parseFloat(value);
  }, []);

  // Function to validate and update effect
  const updateEffect = useCallback(
    async (
      toScaleValue: string,
      baseScaleValue: string,
      centerXValue: string,
      centerYValue: string,
      animationInDurationValue: string,
      animationOutDurationValue: string,
      animationTypeValue: 'easeInAndOut' | 'linear' | 'bounce'
    ) => {
      const toScaleNum = parseFloat(toScaleValue);
      const baseScaleNum = parseFloat(baseScaleValue);
      const centerXNum = parseFloat(centerXValue);
      const centerYNum = parseFloat(centerYValue);
      const animationInDurationNum = parseFloat(parseFloat(animationInDurationValue).toFixed(2));
      const animationOutDurationNum = parseFloat(parseFloat(animationOutDurationValue).toFixed(2));

      const hasValidChanges =
        !isNaN(toScaleNum) &&
        !isNaN(baseScaleNum) &&
        !isNaN(centerXNum) &&
        !isNaN(centerYNum) &&
        !isNaN(animationInDurationNum) &&
        !isNaN(animationOutDurationNum) &&
        toScaleNum >= 1.0 &&
        toScaleNum <= 1.8 &&
        baseScaleNum >= 1.0 &&
        baseScaleNum <= 1.8 &&
        centerXNum >= 0 &&
        centerXNum <= 1 &&
        centerYNum >= 0 &&
        centerYNum <= 1 &&
        animationInDurationNum >= 0 &&
        animationOutDurationNum >= 0 &&
        animationInDurationNum + animationOutDurationNum <= totalDuration &&
        (toScaleNum !== effect.params.toScale ||
          baseScaleNum !== effect.params.baseScale ||
          centerXNum !== (effect.params.centerX ?? 0.5) ||
          centerYNum !== (effect.params.centerY ?? 0.5) ||
          animationInDurationNum !== (effect.animationInDuration ?? 0) ||
          animationOutDurationNum !== (effect.animationOutDuration ?? 0) ||
          animationTypeValue !== (effect.animationType || 'easeInAndOut')) &&
        !isUpdating;

      if (hasValidChanges) {
        // Create a unique key for this update to prevent duplicate calls
        const updateKey = `${toScaleNum}-${baseScaleNum}-${centerXNum}-${centerYNum}-${animationInDurationNum}-${animationOutDurationNum}-${animationTypeValue}`;

        if (lastUpdateRef.current === updateKey) {
          return; // Skip if this exact update was already sent
        }

        setIsUpdating(true);
        try {
          onSubmit(
            toScaleNum,
            baseScaleNum,
            centerXNum,
            centerYNum,
            animationInDurationNum,
            animationOutDurationNum,
            animationTypeValue
          );
          lastUpdateRef.current = updateKey;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update zoom effect:', error);
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [
      effect.params.toScale,
      effect.params.baseScale,
      effect.params.centerX,
      effect.params.centerY,
      effect.animationInDuration,
      effect.animationOutDuration,
      effect.animationType,
      totalDuration,
      onSubmit,
      isUpdating
    ]
  );

  // Debounced update function
  const debouncedUpdate = useCallback(
    (
      toScaleValue: string,
      baseScaleValue: string,
      centerXValue: string,
      centerYValue: string,
      animationInDurationValue: string,
      animationOutDurationValue: string,
      animationTypeValue: 'easeInAndOut' | 'linear' | 'bounce'
    ) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        updateEffect(
          toScaleValue,
          baseScaleValue,
          centerXValue,
          centerYValue,
          animationInDurationValue,
          animationOutDurationValue,
          animationTypeValue
        );
      }, 300);
    },
    [updateEffect]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleDelete = useCallback(
    (closeTooltip: () => void) => {
      onDelete(effect.id);
      closeTooltip();
    },
    [effect.id, onDelete]
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const isDurationSumExceeded = useCallback(() => {
    const inDuration = parseFloat(animationInDuration) || 0;
    const outDuration = parseFloat(animationOutDuration) || 0;
    return inDuration + outDuration > totalDuration;
  }, [animationInDuration, animationOutDuration, totalDuration]);

  if (effect.type !== EffectType.Zoom) {
    return null;
  }

  return (
    <BaseTooltip triggerRef={triggerRef}>
      {closeTooltip => (
        <div className="min-w-0 max-w-xs space-y-4 transition-all duration-200 ease-in-out">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex shrink-0 items-center gap-2">
              <h3 className="truncate text-sm font-semibold">Zoom</h3>
              <TooltipV2
                tooltipId="zoom-expand-toggle"
                tooltipContent={isExpanded ? 'Hide Target Position' : 'Show Target Position'}
                tooltipElement={
                  <IconButton
                    icon="IconCrosshair"
                    variation={isExpanded ? 'filled' : 'outline'}
                    size="small"
                    onClick={toggleExpanded}
                    trackingId="zoom-expand-toggle"
                    buttonClassName="size-6"
                  />
                }
                size="xsmall"
                place="top"
              />
            </div>
            <TooltipV2
              tooltipId="zoom-delete-button"
              tooltipContent="Delete Effect"
              tooltipElement={
                <IconButton
                  icon="IconTrash"
                  variation="text"
                  size="small"
                  onMouseDown={() => handleDelete(closeTooltip)}
                  trackingId="zoom-delete-button"
                  buttonClassName="size-5 shrink-0 text-gray-400 hover:bg-red-50 hover:text-red-500 focus:ring-red-500"
                />
              }
              size="xsmall"
              place="top"
            />
          </div>

          {/* Zoom Controls - Badge Only Layout */}
          <div className="flex items-center justify-center gap-3">
            {/* Zoom Start Control */}
            <div className="flex flex-1 flex-col items-center gap-2">
              <h4 className="text-sm font-medium text-gray-400">Zoom start</h4>
              <div className="w-20">
                <SpeakerLabelNumberInput
                  value={baseScale}
                  onChange={scaleValue => {
                    setBaseScale(scaleValue);
                    debouncedUpdate(
                      toScale,
                      scaleValue,
                      centerX,
                      centerY,
                      animationInDuration,
                      animationOutDuration,
                      animationType
                    );
                  }}
                  min={100}
                  max={180}
                  step={1}
                  unit="%"
                  defaultValue="1.0"
                  testId="zoom-start-input"
                  ariaLabel="Zoom start percentage"
                  incrementLabel="Increase zoom start"
                  decrementLabel="Decrease zoom start"
                  formatOutput={formatScaleOutput}
                  parseInput={parseScaleInput}
                />
              </div>
            </div>

            {/* Chain/Link Icon */}
            <div className="flex items-center pt-6">
              <IconLink size={16} className="text-gray-400" />
            </div>

            {/* Zoom End Control */}
            <div className="flex flex-1 flex-col items-center gap-2">
              <h4 className="text-sm font-medium text-gray-400">Zoom end</h4>
              <div className="w-20">
                <SpeakerLabelNumberInput
                  value={toScale}
                  onChange={scaleValue => {
                    setToScale(scaleValue);
                    debouncedUpdate(
                      scaleValue,
                      baseScale,
                      centerX,
                      centerY,
                      animationInDuration,
                      animationOutDuration,
                      animationType
                    );
                  }}
                  min={100}
                  max={180}
                  step={1}
                  unit="%"
                  defaultValue="1.0"
                  testId="zoom-end-input"
                  ariaLabel="Zoom end percentage"
                  incrementLabel="Increase zoom end"
                  decrementLabel="Decrease zoom end"
                  formatOutput={formatScaleOutput}
                  parseInput={parseScaleInput}
                />
              </div>
            </div>
          </div>

          {/* Ease Controls - Dual Handle Slider */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-400">Ease In/Out</h4>
            <DualRangeSlider
              min={0}
              max={totalDuration}
              value1={parseFloat(animationInDuration)}
              value2={totalDuration - parseFloat(animationOutDuration)}
              onChange={(easeInEnd, easeOutStart) => {
                const easeInDuration = easeInEnd;
                const easeOutDuration = totalDuration - easeOutStart;

                setAnimationInDuration(easeInDuration.toFixed(2));
                setAnimationOutDuration(easeOutDuration.toFixed(2));

                debouncedUpdate(
                  toScale,
                  baseScale,
                  centerX,
                  centerY,
                  easeInDuration.toFixed(2),
                  easeOutDuration.toFixed(2),
                  animationType
                );
              }}
            />
          </div>

          {/* Zoom Effect Graph - Show only when collapsed */}
          {!isExpanded && (
            <ZoomEffectGraph
              baseScale={parseFloat(baseScale)}
              toScale={parseFloat(toScale)}
              animationInDuration={parseFloat(animationInDuration)}
              animationOutDuration={parseFloat(animationOutDuration)}
              totalDuration={totalDuration}
            />
          )}

          {/* Target Position - Conditional on Expand State */}
          {isExpanded && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-400">Target position</h4>
              <div
                className="relative h-32 w-full cursor-crosshair rounded-lg border border-gray-300 bg-white"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, #d1d5db 1px, transparent 1px),
                    linear-gradient(to bottom, #d1d5db 1px, transparent 1px)
                  `,
                  backgroundSize: '20% 33.33%'
                }}
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
                  setCenterX(x.toFixed(2));
                  setCenterY(y.toFixed(2));
                  debouncedUpdate(
                    toScale,
                    baseScale,
                    x.toFixed(2),
                    y.toFixed(2),
                    animationInDuration,
                    animationOutDuration,
                    animationType
                  );
                }}
              >
                {/* Target indicator */}
                <div
                  className="pointer-events-none absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gray-400 bg-white shadow-lg"
                  style={{
                    left: `${parseFloat(centerX) * 100}%`,
                    top: `${parseFloat(centerY) * 100}%`
                  }}
                />

                {/* X/Y coordinates positioned inside grid at bottom left */}
                <div className="pointer-events-none absolute bottom-2 left-2 text-xs text-gray-500">
                  X: {Math.round(parseFloat(centerX) * 100)}% Y: {Math.round(parseFloat(centerY) * 100)}%
                </div>
              </div>
            </div>
          )}

          {/* Duration Warning */}
          {isDurationSumExceeded() && (
            <div className="rounded bg-red-50 p-2">
              <p className="text-xs text-red-600">Animation durations exceed total duration ({totalDuration}s)</p>
            </div>
          )}

          {isUpdating && (
            <div className="text-center">
              <span className="text-xs text-gray-500">Updating...</span>
            </div>
          )}
        </div>
      )}
    </BaseTooltip>
  );
};

export default ZoomEffectTooltip;
